import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { Base } from "../../../framework/base";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { AppUtils } from "../../../models/common/appUtils";
import { PlannedWorkShiftDto } from "../../../models/plannedWorkShift/plannedWorkShift";
import { WorkShiftDto } from "../../../models/workShift/workShift";
import { WorkShiftTimeSlotItem } from "../../../models/workShitTimeSlot/workShiftTimeSlotItem";
import _groupBy from "lodash/groupBy";
import { LinkButton } from "../../framework/linkButton";
import { SidebarDetailsRow } from "../../layout/SidebarDetailsRow";

interface WorkShiftPlanningSidebarContentProps {
    shift: PlannedWorkShiftDto;
    actualShifts: WorkShiftDto[];
    plannedShifts: PlannedWorkShiftDto[];
}

export const WorkShiftPlanningSidebarContent = ({
    shift,
    actualShifts,
    plannedShifts,
}: WorkShiftPlanningSidebarContentProps) => {
    const { t } = useTranslation();

    const overlappingActualShifts = useMemo(
        () =>
            actualShifts.filter((s) =>
                Base.isOverlappingDate(
                    shift.startTime,
                    shift.endTime,
                    // Actual shift timezone should not matter, only timestamps need to overlap
                    Base.dateStrToDayjsIgnoreTimezone(s.startTime),
                    Base.dateStrToDayjsIgnoreTimezone(s.endTime)
                )
            ),
        [actualShifts]
    );

    const shiftLabel = useMemo(
        () =>
            `${Base.dayjsToDateStrWithWeekday(
                shift.startTime
            )} ${Base.dayjsToTimeStr(shift.startTime)}-${
                Base.isSameDay(shift.startTime, shift.endTime)
                    ? Base.dayjsToTimeStr(shift.endTime)
                    : `${Base.dayjsToDateStrWithWeekday(
                          shift.endTime
                      )} ${Base.dayjsToTimeStr(shift.endTime)}`
            }`,
        [shift.startTime, shift.endTime]
    );

    const plannedHours = useMemo(
        () =>
            AppUtils.getDurationStrByDurationMin(
                Base.dayjsDiffInMinutes(shift.startTime, shift.endTime),
                true
            ),
        [shift.startTime, shift.endTime]
    );

    const actualHours = useMemo(
        () =>
            AppUtils.getDurationStrByDurationMin(
                overlappingActualShifts.reduce(
                    (acc, cur) =>
                        acc +
                        Base.dayjsDiffInMinutes(cur.startTime, cur.endTime),
                    0
                ),
                true
            ),
        [overlappingActualShifts]
    );

    const firstShift = overlappingActualShifts[0];

    const vehicleRows = useMemo(
        () =>
            groupShiftSlots(
                overlappingActualShifts,
                t("workShiftPlanning.noVehicle"),
                (s) =>
                    !!s.vehicleId
                        ? `${s.vehicleBrand} ${s.vehicleRegisterNumber}`
                        : "null",
            ),
        [overlappingActualShifts]
    );

    const customerRows = useMemo(
        () =>
            groupShiftSlots(
                overlappingActualShifts,
                t("workShiftPlanning.noCustomer"),
                (s) => s.customerName ?? "null"
            ),
        [overlappingActualShifts]
    );

    const costCenterRows = useMemo(
        () =>
            groupShiftSlots(
                overlappingActualShifts,
                t("workShiftPlanning.noCostCenter"),
                (s) => s.costCenterName ?? "null"
            ),
        [overlappingActualShifts]
    );

    const workTimeTypeRows = useMemo(
        () =>
            groupShiftSlots(
                overlappingActualShifts,
                t("workShiftPlanning.noWorkTimeType"),
                (s) => s.workTimeTypeName ?? "null"
            ),
        [overlappingActualShifts]
    );

    const plannedTimeBefore = useMemo(() => {
        const endBeforeWithin24Hours = plannedShifts.filter(
            (p) =>
                // Ends before planned shift
                Base.isSameOrBefore(p.endTime, shift.startTime) &&
                // End max 24 hours before planned shift
                Base.isSameOrAfter(
                    Base.dateAddDays(p.endTime, 1),
                    shift.startTime,
                ),
        );

        if (endBeforeWithin24Hours.length === 0) {
            return "-";
        }

        return AppUtils.getDurationStrByDurationMin(
            Base.dayjsDiffInMinutes(
                Base.maxDate(endBeforeWithin24Hours.map((p) => p.endTime)),
                shift.startTime
            ),
            true
        );
    }, [plannedShifts, shift.startTime]);

    const plannedTimeAfter = useMemo(() => {
        const startAfterWithin24Hours = plannedShifts.filter(
            (p) =>
                // Starts after planned shift
                Base.isSameOrAfter(p.startTime, shift.endTime) &&
                // Start max 24 hours after planned shift
                Base.isSameOrBefore(
                    Base.dateAddDays(p.startTime, -1),
                    shift.endTime,
                ),
        );

        if (startAfterWithin24Hours.length === 0) {
            return "-";
        }

        return AppUtils.getDurationStrByDurationMin(
            Base.dayjsDiffInMinutes(
                shift.endTime,
                Base.minDate(startAfterWithin24Hours.map((p) => p.startTime))
            ),
            true
        );
    }, [plannedShifts, shift.endTime]);

    const actualTimeBefore = useMemo(() => {
        const actualStart = overlappingActualShifts[0]?.startTime;

        if (!actualStart) return "-";

        const endBeforeWithin24Hours = actualShifts
            .map((s) => Base.dateStrToDayjsIgnoreTimezone(s.endTime))
            .filter(
                (end) =>
                    // Ends before actual shift
                    Base.isSameOrBefore(end, actualStart) &&
                    // End max 24 hours before actual shift
                    Base.isSameOrAfter(Base.dateAddDays(end, 1), actualStart),
            );

        if (endBeforeWithin24Hours.length === 0) {
            return "-";
        }

        return AppUtils.getDurationStrByDurationMin(
            Base.dayjsDiffInMinutes(Base.maxDate(endBeforeWithin24Hours), actualStart),
            true
        );
    }, [actualShifts, overlappingActualShifts]);

    const actualTimeAfter = useMemo(() => {
        const actualEnd =
            overlappingActualShifts[overlappingActualShifts.length - 1]
                ?.endTime;

        if (!actualEnd) return "-";

        const startAfterWithin24Hours = actualShifts
            .map((s) => Base.dateStrToDayjsIgnoreTimezone(s.startTime))
            .filter(
                (start) =>
                    // Starts after actual shift
                    Base.isSameOrAfter(start, actualEnd) &&
                    // Start max 24 hours after actual shift
                    Base.isSameOrBefore(Base.dateAddDays(start, -1), actualEnd),
            );

        if (startAfterWithin24Hours.length === 0) {
            return "-";
        }

        return AppUtils.getDurationStrByDurationMin(
            Base.dayjsDiffInMinutes(actualEnd, Base.minDate(startAfterWithin24Hours)),
            true
        );
    }, [actualShifts, overlappingActualShifts]);

    return (
        <>
            <Typography variant="h3" color="primary.dark" mb={3}>
                {shiftLabel}
            </Typography>
            <Typography variant="h3" color="primary.dark" mb={1}>
                {t("workShiftPlanning.sidebar.workTime")}
            </Typography>
            <SidebarDetailsRow
                title={t("workShiftPlanning.sidebar.planned")}
                text={plannedHours}
                color="primary.main"
            />
            <SidebarDetailsRow
                title={t("workShiftPlanning.sidebar.actual")}
                text={actualHours}
            />
            <SidebarSubheader title={t("employee.employee")} />
            <SidebarDetailsRow title={shift.employeeName} />
            <SidebarSubheader title={t("vehicle.fleet")} />
            {vehicleRows.map((r) => (
                <SidebarDetailsRow
                    key={r.label}
                    title={r.label}
                    text={r.hours}
                    italic={r.italic}
                />
            ))}
            <SidebarSubheader title={t("customer.customer")} />

            {customerRows.map((r) => (
                <SidebarDetailsRow
                    key={r.label}
                    title={r.label}
                    text={r.hours}
                    italic={r.italic}
                />
            ))}
            <SidebarSubheader title={t("workTime.costCenter")} />

            {costCenterRows.map((r) => (
                <SidebarDetailsRow
                    key={r.label}
                    title={r.label}
                    text={r.hours}
                    italic={r.italic}
                />
            ))}
            <SidebarSubheader title={t("workTime.workTimeType")} />

            {workTimeTypeRows.map((r) => (
                <SidebarDetailsRow
                    key={r.label}
                    title={r.label}
                    text={r.hours}
                    italic={r.italic}
                />
            ))}
            <Typography variant="h3" color="primary.dark" mt={3} mb={1}>
                {t("workShiftPlanning.sidebar.timeBetweenShifts")}
            </Typography>
            <SidebarSubheader
                title={t("workShiftPlanning.sidebar.beforeShift")}
            />
            <Box
                sx={{
                    display: "flex",
                }}
            >
                <Tooltip
                    title={t(
                        "workShiftPlanning.sidebar.beforeShiftPlannedTooltip",
                    )}
                >
                    <Typography sx={{ color: "primary.main", mr: 1 }}>
                        {plannedTimeBefore}
                    </Typography>
                </Tooltip>
                <Typography sx={{ color: "dark.main" }}>/</Typography>
                <Tooltip
                    title={t(
                        "workShiftPlanning.sidebar.beforeShiftActualTooltip",
                    )}
                >
                    <Typography sx={{ color: "success.main", ml: 1 }}>
                        {actualTimeBefore}
                    </Typography>
                </Tooltip>
            </Box>
            <SidebarSubheader
                title={t("workShiftPlanning.sidebar.afterShift")}
            />
            <Box
                sx={{
                    display: "flex",
                }}
            >
                <Tooltip
                    title={t(
                        "workShiftPlanning.sidebar.afterShiftPlannedTooltip",
                    )}
                >
                    <Typography sx={{ color: "primary.main", mr: 1 }}>
                        {plannedTimeAfter}
                    </Typography>
                </Tooltip>
                <Typography sx={{ color: "dark.main" }}>/</Typography>
                <Tooltip
                    title={t(
                        "workShiftPlanning.sidebar.afterShiftActualTooltip",
                    )}
                >
                    <Typography sx={{ color: "success.main", ml: 1 }}>
                        {actualTimeAfter}
                    </Typography>
                </Tooltip>
            </Box>
            {!!firstShift ? (
                <LinkButton
                    target="_blank"
                    to={`/worktimepage/details/${firstShift.employeeId}/${firstShift.effectiveDate}/hourbooking/${firstShift.workShiftId}`}
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    sx={{ mt: 2 }}
                >
                    {t("workTime.workTimeEntries")}
                </LinkButton>
            ) : null}
        </>
    );
};

interface SidebarSubheaderProps {
    title: string;
}

export const SidebarSubheader = ({ title }: SidebarSubheaderProps) => (
    <Typography variant="h4" color="dark.main" mt={2}>
        {title}:
    </Typography>
);

interface DetailsItem {
    label: string;
    hours: string;
    italic?: boolean;
}

const groupShiftSlots = (
    shifts: WorkShiftDto[],
    emptyLabel: string,
    accessor: (slot: WorkShiftTimeSlotItem) => string
) => {
    const slotsByVehicle = _groupBy(
        shifts.flatMap((s) => s.workShiftTimeSlots),
        accessor
    );

    let result: DetailsItem[] = [];

    result = Object.keys(slotsByVehicle)
        .filter((k) => k !== "null")
        .sort()
        .map((k) => ({
            label: k,
            hours: AppUtils.getDurationStrByDurationMin(
                slotsByVehicle[k].reduce(
                    (acc, cur) =>
                        acc +
                        new WorkShiftTimeSlotItem(cur).getDurationMinutes(),
                    0
                ),
                true
            ),
        }));

    if (slotsByVehicle["null"]?.length > 0) {
        result.push({
            label: emptyLabel,
            italic: true,
            hours: AppUtils.getDurationStrByDurationMin(
                slotsByVehicle["null"].reduce(
                    (acc, cur) =>
                        acc +
                        new WorkShiftTimeSlotItem(cur).getDurationMinutes(),
                    0
                ),
                true
            ),
        });
    }

    return result;
};